import { useTranslation } from "react-i18next";
import { PlusCircle, Trash2 } from "lucide-react";
import { OpeningHours, OpeningSlot } from "@/types/forms/auth/registerFormData.form";
import { DAYS } from "./DaysPicker";

const TIME_INPUT_CLASS =
  "w-fit rounded-md border border-gray-300 px-1 sm:px-2 py-1 text-xs sm:text-sm";

type DayTimeSlotsProps = {
  openingHours: OpeningHours;
  selectedDays: number[];
  onAddSlot: (dayIndex: number) => void;
  onRemoveSlot: (dayIndex: number, slotIndex: number) => void;
  onUpdateSlot: (
    dayIndex: number,
    slotIndex: number,
    field: "open" | "close",
    value: string,
  ) => void;
};

export const DayTimeSlots = ({
  openingHours,
  selectedDays,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
}: DayTimeSlotsProps) => {
  const { t } = useTranslation("enums");
  const activeDays = DAYS.filter((day) => selectedDays.includes(day.key));

  return (
    <div className="flex flex-col gap-4">
      {activeDays.map((day) => (
        <DaySection
          key={day.key}
          dayKey={day.key}
          dayLabel={t(`pickupDay.${day.day}`)}
          slots={openingHours[day.key.toString()] ?? []}
          onAddSlot={() => onAddSlot(day.key)}
          onRemoveSlot={(slotIndex) => onRemoveSlot(day.key, slotIndex)}
          onUpdateSlot={(slotIndex, field, value) => onUpdateSlot(day.key, slotIndex, field, value)}
        />
      ))}
    </div>
  );
};

type DaySectionProps = {
  dayKey: number;
  dayLabel: string;
  slots: OpeningSlot[];
  onAddSlot: () => void;
  onRemoveSlot: (slotIndex: number) => void;
  onUpdateSlot: (slotIndex: number, field: "open" | "close", value: string) => void;
};

const DaySection = ({
  dayKey,
  dayLabel,
  slots,
  onAddSlot,
  onRemoveSlot,
  onUpdateSlot,
}: DaySectionProps) => (
  <div key={dayKey} className="flex flex-row gap-2">
    <p className="w-14 pt-1! text-xs font-medium text-gray-700 sm:w-22 sm:text-sm">{dayLabel}</p>
    <div className="flex flex-col gap-2">
      {slots.map((slot, slotIndex) => (
        <TimeSlotRow
          key={slotIndex}
          slot={slot}
          slotIndex={slotIndex}
          isLastSlot={slotIndex === slots.length - 1}
          canDelete={slots.length > 1}
          onUpdate={(field, value) => onUpdateSlot(slotIndex, field, value)}
          onRemove={() => onRemoveSlot(slotIndex)}
          onAdd={onAddSlot}
        />
      ))}
    </div>
  </div>
);

type TimeSlotRowProps = {
  slot: OpeningSlot;
  slotIndex: number;
  isLastSlot: boolean;
  canDelete: boolean;
  onUpdate: (field: "open" | "close", value: string) => void;
  onRemove: () => void;
  onAdd: () => void;
};

const TimeSlotRow = ({
  slot,
  isLastSlot,
  canDelete,
  onUpdate,
  onRemove,
  onAdd,
}: TimeSlotRowProps) => (
  <div className="flex flex-row items-center gap-2">
    <input
      type="time"
      value={slot.open}
      onChange={(e) => onUpdate("open", e.target.value)}
      className={TIME_INPUT_CLASS}
    />
    <span>-</span>
    <input
      type="time"
      value={slot.close}
      onChange={(e) => onUpdate("close", e.target.value)}
      className={TIME_INPUT_CLASS}
    />
    {canDelete && (
      <Trash2
        className="h-5 w-5 cursor-pointer text-gray-600 hover:text-red-500"
        onClick={onRemove}
      />
    )}
    {isLastSlot && (
      <PlusCircle
        className="h-5 w-5 cursor-pointer text-blue-600 hover:text-blue-700"
        onClick={onAdd}
      />
    )}
  </div>
);
